import axios from 'axios';
import type { AxiosInstance, AxiosRequestConfig } from 'axios';
import { auth } from '@/lib/firebase';

// Extend AxiosRequestConfig to include our custom properties
interface ExtendedAxiosRequestConfig extends AxiosRequestConfig {
  _retryAttempted?: boolean;
}

export class ApiClient {
  private client: AxiosInstance;
  private readonly REQUEST_TIMEOUT = 60000; // 60 seconds

  constructor(baseURL: string = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8082') {
    this.client = axios.create({
      baseURL,
      timeout: this.REQUEST_TIMEOUT,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    this.setupInterceptors();
  }

  private setupInterceptors() {
    // Add request interceptor for auth token
    this.client.interceptors.request.use(
      async (config) => {
        const token = await this.getAuthToken();
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
      },
      (error) => {
        return Promise.reject(error);
      }
    );

    // Add response interceptor for error handling
    this.client.interceptors.response.use(
      (response) => response,
      async (error) => {
        // Handle 401 errors by refreshing token and retrying
        if (error.response?.status === 401 && !(error.config as ExtendedAxiosRequestConfig)._retryAttempted) {
          console.log('[ApiClient] 401 error, attempting token refresh and retry...');
          try {
            const user = auth.currentUser;
            if (user) {
              const newToken = await user.getIdToken(true); // Force refresh
              error.config.headers.Authorization = `Bearer ${newToken}`;
              (error.config as ExtendedAxiosRequestConfig)._retryAttempted = true;
              return this.client.request(error.config);
            }
          } catch (refreshError) {
            console.error('[ApiClient] Token refresh failed during retry:', refreshError);
          }
        }

        // Redirect to login on 401
        if (error.response?.status === 401) {
          if (window.location.pathname !== '/login') {
            window.location.href = '/login';
          }
        }

        return Promise.reject(error);
      }
    );
  }

  private async getAuthToken(): Promise<string | null> {
    const user = auth.currentUser;
    if (user) {
      try {
        return await user.getIdToken(false);
      } catch (error) {
        console.error('[ApiClient] Error getting auth token:', error);
        return null;
      }
    }
    return null;
  }

  async get<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.client.get<T>(url, config);
    return response.data;
  }

  async post<T>(url: string, data?: unknown, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.client.post<T>(url, data, config);
    return response.data;
  }

  async put<T>(url: string, data?: unknown, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.client.put<T>(url, data, config);
    return response.data;
  }

  async delete<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.client.delete<T>(url, config);
    return response.data;
  }

  async patch<T>(url: string, data?: unknown, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.client.patch<T>(url, data, config);
    return response.data;
  }
}

export const api = new ApiClient();
